import './subscriptions.scss'
import text from './text.json'
import ResponsivePageTitle from '../../components/core/title/ResponsivePageTitle'
import {PageSubTitle, PageTitle} from '../../components/core/title'
import ContentLoader from '../../components/core/loader'
import EmptyContent from '../resources/EmptyContent'
import CustomSelect from '../../components/core/customSelect'
import Footer from '../../components/layouts/footer'
import Table from '../../components/core/table'
import {useDispatch, useSelector} from 'react-redux'
import {getStatuspages} from '../../stores/statuspage'
import {useEffect, useState, useMemo} from 'react'
import StatuspageApiService from '../../api/statuspage'
import {colors} from '../../utils/colors'
import {
    LoadMoreIcon,
    EnvelopIcon,
    HelpIcon,
    AddTaskIcon,
    AddTaskWithCursor
} from '../../icons'
import moment from 'moment'
import {toast} from 'react-toastify'
import TextHelper from '../../utils/text'
import {useNavigate} from 'react-router-dom'
import {routes} from '../../routes'
import LimitManager from '../../utils/limitManager'
import Tooltip from '../../components/core/tooltip'
import {calculateRemaining} from '../../utils/limits'
import classNames from 'classnames'
import RenderedForPaid from '../../components/pages/RenderedForPaid'
import {ImgButton} from '../../components/core/button'

const channelTypes = {
    'email': {
        'type': 'email',
        'text': text.email,
        'icon': <EnvelopIcon className="table-icon"/>
    }
}

const getChannelType = channel => {
    return <span className="min-width-150">
        <span className="type">
            {channelTypes[channel]?.icon}
            <span>{channelTypes[channel]?.text}</span>
        </span>
    </span>
}

const getChannelStatus = (channel, isDarkMode) => {
    return <span className="min-width-150">
        <span
            className={classNames('channel-status', {
                'channel-verified': channel?.verified,
                'channel-unverified': !channel?.verified,
                'dark': isDarkMode
            })}>{channel?.verified ? text.verified : text.unverified}</span>
    </span>
}
const DEFAULT_LIMIT = 50
const DEFAULT_QUERY = `?limit=${DEFAULT_LIMIT}`

const StatuspageSubscriptions = () => {
    const dispatch = useDispatch()
    const navigate = useNavigate()
    const [filteredStatuspages, setFilteredStatuspages] = useState([])
    const [selectedStatuspage, setSelectedStatuspage] = useState()
    const [isFetching, setIsFetching] = useState(false)
    const [subscriptionsData, setSubscriptionsData] = useState({
        lastId: '',
        subscriptions: []
    })
    const [isLoadMoreEnabled, setIsLoadMoreEnabled] = useState(true)
    const [buttonLoaderIsActive, setButtonLoaderIsActive] = useState(false)
    const [query, setQuery] = useState(DEFAULT_QUERY)
    const statuspages = useSelector(state => state.statuspage.statuspages)
    const isProcessing = useSelector(state => state.statuspage.isProcessing)
    const isDarkMode = useSelector(state => state.sidebar.isDarkMode)
    const totalSubscriptionsCount = statuspages?.reduce((acc, item) => acc + item?.subscription_count, 0)
    const statuspageSubscriptionsLimit = useMemo(() => LimitManager.getStatusPageSubscriptionsCount(), [])

    const getPaidText = () => {
        return calculateRemaining(statuspageSubscriptionsLimit, totalSubscriptionsCount || 0) > 0 && TextHelper.replaceAllChars(
            TextHelper.replaceAllChars(
                text.subscriptions_for_paid, ':remaining', calculateRemaining(statuspageSubscriptionsLimit, totalSubscriptionsCount || 0)
            ),
            ':limit',
            statuspageSubscriptionsLimit
        )
    }

    const getLimitIsExceeded = () => {
        return totalSubscriptionsCount >= statuspageSubscriptionsLimit || statuspageSubscriptionsLimit === 0
    }

    const getStatuspageSubscriptions = async(query, isLoadMore = false) => {
        isLoadMore && setButtonLoaderIsActive(true)
        !isLoadMore && setIsFetching(true)
        try {
            const {data} = await StatuspageApiService.getStatuspageSubscriptions(selectedStatuspage?.value, query)
            const lastId = data?.meta?.last_id
            const pushData = data?.data?.subscriptions?.some(item => subscriptionsData?.subscriptions?.some(localData => localData.id === item.id))
            if (data?.data?.subscriptions) {
                isLoadMore ? setSubscriptionsData({
                    lastId,
                    subscriptions: !pushData && (subscriptionsData?.subscriptions?.concat(data?.data?.subscriptions))
                }) : setSubscriptionsData({
                    lastId,
                    subscriptions: data?.data?.subscriptions
                })
            }
            if (!data?.meta || data?.meta?.count < DEFAULT_LIMIT) {
                setIsFetching(false)
                setIsLoadMoreEnabled(false)
                return
            }
            setIsLoadMoreEnabled(true)
        } catch (e) {
            toast.error(e?.response?.message)
        }
        setIsFetching(false)
        setButtonLoaderIsActive(false)
    }

    const tableHeader = [
        {
            label: 'Status',
            className: 'min-width-150'
        },
        {
            label: 'Type',
            className: 'min-width-150'
        },
        {
            label: 'Channel',
            className: 'min-width-250'
        },
        {
            label: 'Created at',
            className: 'min-width-150'
        }
    ]

    const tbodyData = subscriptionsData?.subscriptions?.map(item => {
        return [
            getChannelStatus(item, isDarkMode),
            getChannelType(item?.channel_type),
            <span className="min-width-250">{item?.channel_value}</span>,
            <span className="min-width-150">{moment(item?.created_at).format('YYYY-MM-DD HH:mm')}</span>
        ]
    })

    const loadMore = async() => {
        await setButtonLoaderIsActive(true)
        setQuery(DEFAULT_QUERY + `&last_id=${subscriptionsData?.lastId}`)
    }

    useEffect(() => {
        dispatch(getStatuspages())
    }, [])

    useEffect(() => {
        setFilteredStatuspages(statuspages?.map(page => {
            return {
                label: `${page?.name} ${page?.subscription_count ? `(${page?.subscription_count})` : ''}`,
                value: page?.id
            }
        }))
    }, [statuspages])

    useEffect(() => {
        setSubscriptionsData({
            lastId: '',
            subscriptions: []
        })
        selectedStatuspage && getStatuspageSubscriptions(DEFAULT_QUERY, false)
    }, [selectedStatuspage])

    useEffect(() => {
        selectedStatuspage && getStatuspageSubscriptions(query, true)
    }, [query])

    return (
        <main className={classNames('main subscriptions', {
            dark: isDarkMode
        })}>
            <div className="content-container">
                <ResponsivePageTitle/>
                <div className="content-title">
                    <div className="left-side">
                        <div>
                            <PageSubTitle>
                                <p className={classNames('section', {
                                    dark: isDarkMode
                                })}>{text.section}</p>
                            </PageSubTitle>
                            <PageTitle>
                                <h1  className={classNames('page-title', {
                                    dark: isDarkMode
                                })}>{text.subscriptions}</h1>
                            </PageTitle>
                        </div>
                    </div>
                </div>
                <RenderedForPaid
                    forPaidText={getPaidText() || text.subscriptions_for_paid_general}
                    isExceeded={getLimitIsExceeded()}
                    isTextWithLimits={!!getPaidText()}
                />
                {statuspages?.length > 0 &&
                    <div className="statuspage-select mt-1">
                        <CustomSelect
                            placeholder={text.statuspage}
                            options={filteredStatuspages}
                            name="statuspage_id"
                            value={selectedStatuspage}
                            onChange={(name, data) => {
                                setSelectedStatuspage(data)
                            }}
                        />
                        <Tooltip placement="right" interactive trigger="click"
                            content={text.statuspage_select_info}>
                            <HelpIcon/>
                        </Tooltip>
                    </div>
                }
                {
                    (isFetching || isProcessing) ? <ContentLoader /> :
                        statuspages?.length > 0 ?
                            subscriptionsData?.subscriptions?.length > 0 ?
                                <>
                                    <div className="content-body">
                                        <Table
                                            tableHeader={tableHeader}
                                            tbodyData={tbodyData}
                                            data={subscriptionsData?.subscriptions}
                                        />
                                    </div>
                                    {
                                        isLoadMoreEnabled &&
                                        <ImgButton
                                            onClick={() => loadMore()}
                                            type="button"
                                            className={`load-button ${buttonLoaderIsActive ? 'active' : ''}`}
                                        >
                                            <LoadMoreIcon stroke={colors.colorMuted} />
                                            {text.load_more}
                                        </ImgButton>
                                    }
                                </> : <>
                                    <EmptyContent
                                        message={selectedStatuspage ? text.subscriptions_not_found : text.select_statuspage}
                                        hideButton={true}
                                        icon={<AddTaskWithCursor/>}
                                    />
                                </> :
                            <EmptyContent
                                message={text.empty_content_text}
                                hideButton={false}
                                buttonText={text.add_new_page}
                                onclick={() => navigate(routes.create_statuspage.path)}
                                icon={<AddTaskIcon/>}
                            />

                }
            </div>
            <Footer/>
        </main>
    )
}

export default StatuspageSubscriptions
